import React, { useState, useEffect, useRef } from "react";
import { Helmet } from "react-helmet";
import { useForm } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";
import { useMutation, useManualQuery } from "graphql-hooks";

import { Link as RouterLink } from "react-router-dom";
import {
  Box,
  Card,
  CardContent,
  Container,
  Divider,
  Link,
  Typography,
  Button,
  FormHelperText,
  TextField,
  makeStyles,
} from "@material-ui/core";
import Alert from "@mui/material/Alert";
import logoImg from "../../assets/logo.png";

import { ACTIVATE_TENANTUSER_MUTATION,
         GET_USER_DATA_BY_ACTIVATION_CODE_QUERY } from "../../graphQL/user_qry";

import { welcome_page_text, project_url } from "../../constants/Data";

import { Visibility, VisibilityOff } from "@mui/icons-material";
import { InputAdornment, IconButton } from "@mui/material";


const useStyles = makeStyles(theme => ({
  cardContainer: {
    paddingBottom: 80,
    paddingTop: 40,
    maxWidth: 910,
    minHeight: 510,
  },
  cardContainerTablet: {
    paddingBottom: 38,
    paddingTop: 68,
    paddingLeft: 48,
    paddingRight: 48,
    maxWidth: 900,
    height: 488,
  },
  cardContent: {
    padding: theme.spacing(4),
    display: "flex",
    flexDirection: "column",
    paddingTop: 40,
    width: 430,
  },
  cardContentTablet: {
    padding: theme.spacing(4),
    display: "flex",
    flexDirection: "column",
    minHeight: 440,
  },
  cardTablet: {
    display: "flex",
    flexDirection: "column",
    minHeight: 440,
  },
  cardMobile: {
    display: "flex",
    flexDirection: "column",
    minHeight: 400,
  },
  cardContentMobile: {
    padding: theme.spacing(2),
    display: "flex",
    flexDirection: "column",
    paddingTop: 40,
    minHeight: 400,
    //  maxWidth: 700,
  },
  textFieldStyle: {
    marginBottom: 5,
    // marginTop: 30,
    "& .MuiInputLabel-outlined": {
      fontSize: 16,
    },
    "& .MuiOutlinedInput-root": {
      borderRadius: 10,
      fontSize: 16,
    },
  },
  textFieldStyleMobile: {
    marginBottom: 5,
    // marginTop: 30,
    "& .MuiInputLabel-outlined": {
      fontSize: 14,
    },
    "& .MuiOutlinedInput-root": {
      borderRadius: 10,
      fontSize: 14,
    },
  },
  errorStyle: {
    marginTop: 0,
    fontSize: 14, //16,
    fontFamily: "Arial",
  },
  errorStyleMobile: {
    marginTop: 0,
    fontSize: 12,
    fontFamily: "Arial",
  },
  successStyle: {
    color: "#212121",
    backgroundColor: "#F0FEF4",
    borderRadius: 12,
    padding: 16,
    fontSize: 16,
    fontFamily: "Arial",
    lineHeight: 1.4,
  },
  successStyleMobile: {
    color: "#212121",
    backgroundColor: "#F0FEF4",
    borderRadius: 12,
    padding: 16,
    fontSize: 14,
    fontFamily: "Arial",
    lineHeight: 1.4,
  },
  buttonStyle: {
    borderRadius: 12,
    width: "158px",
    height: "48px",
    textTransform: "none",
    "&:active": {
      backgroundColor: "#0D47A1",
    },
    "&:hover": {
      backgroundColor: "#0D47A1",
    },
  },
}));

const sleep = ms => new Promise(resolve => setTimeout(resolve, ms));

const AccountActivation = () => {
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({ mode: "all" });

  const navigate = useNavigate();
  const form = useRef(null);

  const classes = useStyles();
  const [state, setState] = useState({
    mobileView: false,
    tabletView: false,
  });
  const { mobileView, tabletView } = state;

  const [errorMsg, setErrorMsg] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [successMsg, setSuccessMsg] = useState("");
  const [password, setPassword] = useState("");
  const [retypePassword, setRetypePassword] = useState("");
  const { activationCode } = useParams();

  const [showPassword, setShowPassword] = useState(false);
  const [showRepeatPassword, setShowRepeatPassword] = useState(false);

  const handleTogglePassword = () => {
    setShowPassword((prev) => !prev);    
  };

  const handleToggleRepeatPassword = () => {
    setShowRepeatPassword((prev) => !prev);    
  };

  const [activateTenantUser] = useMutation(ACTIVATE_TENANTUSER_MUTATION);
  const [getUserDataByActivationCode] = useManualQuery(GET_USER_DATA_BY_ACTIVATION_CODE_QUERY, {
    fetchPolicy: "network-only",
  });

  useEffect(() => {
    const setResponsiveness = () => {
      return window.innerWidth < 768
        ? setState(() => ({ mobileView: true }))
        : window.innerWidth < 821 && window.innerWidth > 767
        ? setState(() => ({ mobileView: false, tabletView: true }))
        : setState(() => ({ tabletView: false }));
    };
    setResponsiveness();
    window.addEventListener("resize", () => setResponsiveness());
    return () => {
      window.removeEventListener("resize", () => setResponsiveness());
    };
  }, []);
  
  useEffect(() => {
    (() => fetchUserDataByActivationCode())();
  }, []);
  
  const fetchUserDataByActivationCode = async () => {
    const {
      loading: userDataLoading,
      error: userDataError,
      data: userData,
    } = await getUserDataByActivationCode(
      {
        variables: {         
          activationCode: activationCode
      }});
    if (!userDataLoading && userData && userData.getUserDataByActivationCode) {
      console.log(userData);
      setFirstName(userData.getUserDataByActivationCode.firstName);
      setLastName(userData.getUserDataByActivationCode.lastName);
    }
    if (
      userDataError &&
      userDataError.graphQLErrors &&
      userDataError.graphQLErrors[0].message === "Signature has expired"
    ) {
      navigate(`${project_url}/signin`);
    }
  };

  setTimeout(() => {
    setSuccessMsg("");
    setErrorMsg("");
  }, 30000);

  /*##########################################################################
  ## Const : onSubmit
  ## Description : Complete the sumbit functionality of the data form 
  ## Author : RR
  ## Date: : 2024/12/28
  ##########################################################################*/

  const onSubmit = async formData => {
    await sleep(2000);

    if (formData.password === formData.retypePassword) {
      const { data, error } = await activateTenantUser({
        variables: {
          password: formData.password,
          activationCode: activationCode,
        },
      });
      if (error) {
        console.log("Error", error, data);
        setErrorMsg(error.graphQLErrors[0].message);
        setTimeout("");
      } else {
        console.log("Success");
        console.log(formData.password)
        setSuccessMsg("Your new password is set.");
        setTimeout("");
        navigate(`${project_url}/account_activation_confirmation/` + activationCode);
      }
    } else {
      setErrorMsg("Password and re-enter password must be the same.");
      setTimeout("");
    }
  };

  /*##########################################################################
  ## Const : passwordChange
  ## Description : The Change Password functionality  
  ## Author : SF
  ## Date: : 2023/03/13
  ##########################################################################*/

  const passwordChange = event => {
    setPassword(event.target.value);
  };

  /*##########################################################################
  ## Const : retypePasswordChange
  ## Description : The Retype Change Password functionality  
  ## Author : SF
  ## Date: : 2023/03/13
  ##########################################################################*/

  const retypePasswordChange = event => {
    setRetypePassword(event.target.value);
  };

  /*##########################################################################
  ## Render Section
  ##########################################################################*/

  return (
    <>
      <title>APIE | Account Activation</title>

      <form ref={form} onSubmit={handleSubmit(onSubmit)}>
        {mobileView && (
          <Card className={classes.cardMobile}>
            <CardContent className={classes.cardContentMobile}>
              <Box alignItems="center" mb={3} flexGrow={1}>
                <Box
                  flexGrow={1}
                  alignItems="center"
                  display="flex"
                  justifyContent="space-between"
                  mb={1}
                >
                  <div>
                    <Typography gutterBottom variant="subtitle2">
                      Account Activation
                    </Typography>
                  </div>
                </Box>
              </Box>
              <Box flexGrow={1} mb={3}>
                <Typography variant="body2">Please enter a password for your account.</Typography>
                <Typography
                  variant="h3"
                  style={{
                    fontSize: 14,
                    display: "flex",
                    alignItems: "left",
                    marginTop: 30,
                  }}
                >
                  Password should be at least 6 characters and numbers, with no special characters.
                </Typography>
                <TextField
                  className={classes.textFieldStyleMobile}
                  fullWidth
                  required
                  //autoFocus
                  label="Enter Password"
                  margin="normal"
                  name="password"
                  type={showPassword ? "text" : "password"}
                  variant="outlined"
                  id="password"
                  InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            onClick={handleTogglePassword}
                            edge="end"
                          >
                            {showPassword ? <VisibilityOff /> : <Visibility />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  {...register("password", { 
                    required: true,
                    pattern: /^[A-Za-z0-9]{6,}$/i,
                  })}
                  onChange={passwordChange}
                />
                {errors.password && errors.password.type === "required" && (
                  <Box ml={2}>
                    <FormHelperText className={classes.errorStyleMobile} error>
                      Password cannot be blank.
                    </FormHelperText>
                  </Box>
                )}
                {errors.password && 
                  errors.password.type === "pattern" && (
                  <Box ml={2}>
                    <FormHelperText className={classes.errorStyleMobile} error>
                      Enter at least 6 characters and numbers, with no special characters
                    </FormHelperText>
                  </Box>
                )}
                <TextField
                    className={classes.textFieldStyle}
                    style={{ marginTop: 30 }}
                    fullWidth
                    required
                    //autoFocus
                    label="Re-enter Password"
                    margin="normal"
                    name="retypePassword"
                    type={showRepeatPassword ? "text" : "password"} 
                    variant="outlined"
                    id="retypePassword"                    
                    {...register("retypePassword", { 
                      required: true,
                      pattern: /^[A-Za-z0-9]{6,}$/i,
                    })}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            onClick={handleToggleRepeatPassword}
                            edge="end"
                          >
                            {showRepeatPassword ? <VisibilityOff /> : <Visibility />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                    onChange={retypePasswordChange}
                  />
                {errors.retypePassword && errors.retypePassword.type === "required" && (
                  <Box ml={2}>
                    <FormHelperText className={classes.errorStyleMobile} error>
                      Re-enter Password cannot be blank.
                    </FormHelperText>
                  </Box>
                )}
                {errors.retypePassword && 
                  errors.retypePassword.type === "pattern" && (
                  <Box ml={2}>
                    <FormHelperText className={classes.errorStyleMobile} error>
                      Enter at least 6 characters and numbers, with no special characters
                    </FormHelperText>
                  </Box>
                )}
                {errorMsg && (
                  <Box ml={2}>
                    <FormHelperText className={classes.errorStyleMobile} error>
                      {errorMsg}
                    </FormHelperText>
                  </Box>
                )}
                {successMsg && (
                  <Box mt={2} className={classes.successStyleMobile}>
                    {successMsg}
                  </Box>
                )}
                <Box mt={successMsg ? 2 : 5} display="flex" justifyContent="center">
                  <Button
                    className={classes.buttonStyle}
                    color="primary"
                    type="submit"
                    variant="contained"
                  >
                    Submit
                  </Button>
                </Box>
              </Box>
            </CardContent>
          </Card>
        )}
        {tabletView && (
          <Container className={classes.cardContainerTablet} maxWidth="sm">
            <Card className={classes.cardTablet}>
              <CardContent className={classes.cardContentTablet}>
                <Box alignItems="center" mb={1}>
                  <Box
                    flexGrow={1}
                    alignItems="center"
                    display="flex"
                    justifyContent="space-between"
                    mb={1}
                  >
                    <div>
                      <Typography gutterBottom variant="subtitle1">
                        Account Activation
                      </Typography>
                    </div>
                  </Box>
                </Box>
                <Box flexGrow={1} mb={3}>
                  <Typography variant="body1">Please enter a password for your account.</Typography>
                  <Typography
                    variant="h3"
                    style={{
                      fontSize: 14,
                      display: "flex",
                      alignItems: "left",
                      marginTop: 30,
                    }}
                  >
                    Password should be at least 6 characters and numbers, with no special characters.
                  </Typography>
                  <TextField
                  className={classes.textFieldStyleMobile}
                  fullWidth
                  required
                  //autoFocus
                  label="Enter Password"
                  margin="normal"
                  name="password"
                  type={showPassword ? "text" : "password"}
                  variant="outlined"
                  id="password"
                  InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            onClick={handleTogglePassword}
                            edge="end"
                          >
                            {showPassword ? <VisibilityOff /> : <Visibility />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  {...register("password", { 
                    required: true,
                    pattern: /^[A-Za-z0-9]{6,}$/i,
                  })}
                  onChange={passwordChange}
                />
                  {errors.password && errors.password.type === "required" && (
                    <Box ml={2}>
                      <FormHelperText className={classes.errorStyle} error>
                        Password cannot be blank.
                      </FormHelperText>
                    </Box>
                  )}
                  {errors.password && 
                    errors.password.type === "pattern" && (
                    <Box ml={2}>
                      <FormHelperText className={classes.errorStyle} error>
                        Enter at least 6 characters and numbers, with no special characters
                      </FormHelperText>
                    </Box>
                  )}
                  <TextField
                    className={classes.textFieldStyle}
                    style={{ marginTop: 30 }}
                    fullWidth
                    required
                    //autoFocus
                    label="Re-enter Password"
                    margin="normal"
                    name="retypePassword"
                    type={showRepeatPassword ? "text" : "password"} 
                    variant="outlined"
                    id="retypePassword"                    
                    {...register("retypePassword", { 
                      required: true,
                      pattern: /^[A-Za-z0-9]{6,}$/i,
                    })}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            onClick={handleToggleRepeatPassword}
                            edge="end"
                          >
                            {showRepeatPassword ? <VisibilityOff /> : <Visibility />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                    onChange={retypePasswordChange}
                  />
                  {errors.retypePassword && errors.retypePassword.type === "required" && (
                    <Box ml={2}>
                      <FormHelperText className={classes.errorStyle} error>
                        Re-enter Password cannot be blank.
                      </FormHelperText>
                    </Box>
                  )}
                  {errors.retypePassword && 
                    errors.retypePassword.type === "pattern" && (
                    <Box ml={2}>
                      <FormHelperText className={classes.errorStyle} error>
                        Enter at least 6 characters and numbers, with no special characters
                      </FormHelperText>
                    </Box>
                  )}
                  {errorMsg && (
                    <Box ml={2}>
                      <FormHelperText className={classes.errorStyleMobile} error>
                        {errorMsg}
                      </FormHelperText>
                    </Box>
                  )}
                  {successMsg && (
                    <Box mt={2} className={classes.successStyle}>
                      {successMsg}
                    </Box>
                  )}
                  <Box mt={successMsg ? 2 : 5} display="flex" justifyContent="center">
                    <Button
                      className={classes.buttonStyle}
                      color="primary"
                      type="submit"
                      variant="contained"
                    >
                      Submit
                    </Button>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Container>
        )}
        {!mobileView && !tabletView && (
          <Container className={classes.cardContainer} maxWidth="sm">
            <Card style={{ display: "flex", flexDirection: "row" }}>
              <CardContent className={classes.cardContent}>
                <Box alignItems="center" mb={3} flexGrow={1}>
                  <Box mb={2}>
                    <img src={logoImg} alt="logo" />
                  </Box>
                  <Typography variant="body1">{welcome_page_text}</Typography>
                </Box>
              </CardContent>
              <Divider orientation="vertical" flexItem />
              <CardContent className={classes.cardContent}>
                <Box
                  //  flexGrow={1}
                  alignItems="center"
                  display="flex"
                  justifyContent="space-between"
                >
                  <Typography gutterBottom variant="subtitle1">
                    Account Activation
                  </Typography>
                </Box>

                <Box flexGrow={1}>
                  <Typography variant="body1">{ firstName } { lastName }, enter your password</Typography>
                  <Typography
                    variant="h3"
                    style={{
                      fontSize: 14,
                      display: "flex",
                      alignItems: "left",
                      marginTop: 30,
                    }}
                  >
                    Password should be at least 6 characters and numbers, with no special characters.
                  </Typography>
                  <TextField
                  className={classes.textFieldStyleMobile}
                  fullWidth
                  required
                  //autoFocus
                  label="Enter Password"
                  margin="normal"
                  name="password"
                  type={showPassword ? "text" : "password"}
                  variant="outlined"
                  id="password"
                  InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            onClick={handleTogglePassword}
                            edge="end"
                          >
                            {showPassword ? <VisibilityOff /> : <Visibility />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  {...register("password", { 
                    required: true,
                    pattern: /^[A-Za-z0-9]{6,}$/i,
                  })}
                  onChange={passwordChange}
                />
                  {errors.password && errors.password.type === "required" && (
                    <Box ml={2}>
                      <FormHelperText className={classes.errorStyle} error>
                        Password cannot be blank.
                      </FormHelperText>
                    </Box>
                  )}
                  {errors.password && 
                    errors.password.type === "pattern" && (
                    <Box ml={2}>
                      <FormHelperText className={classes.errorStyle} error>
                        Enter at least 6 characters and numbers, with no special characters
                      </FormHelperText>
                    </Box>
                  )}
                  <TextField
                    className={classes.textFieldStyle}
                    style={{ marginTop: 30 }}
                    fullWidth
                    required
                    //autoFocus
                    label="Re-enter Password"
                    margin="normal"
                    name="retypePassword"
                    type={showRepeatPassword ? "text" : "password"} 
                    variant="outlined"
                    id="retypePassword"                    
                    {...register("retypePassword", { 
                      required: true,
                      pattern: /^[A-Za-z0-9]{6,}$/i,
                    })}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            onClick={handleToggleRepeatPassword}
                            edge="end"
                          >
                            {showRepeatPassword ? <VisibilityOff /> : <Visibility />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                    onChange={retypePasswordChange}
                  />
                  {errors.retypePassword && errors.retypePassword.type === "required" && (
                    <Box ml={2}>
                      <FormHelperText className={classes.errorStyle} error>
                        Re-enter Password cannot be blank.
                      </FormHelperText>
                    </Box>
                  )}
                  {errors.retypePassword && 
                    errors.retypePassword.type === "pattern" && (
                    <Box ml={2}>
                      <FormHelperText className={classes.errorStyle} error>
                        Enter at least 6 characters and numbers, with no special characters
                      </FormHelperText>
                    </Box>
                  )}
                  {errorMsg && (
                    <Box ml={2}>
                      <FormHelperText className={classes.errorStyleMobile} error>
                        {errorMsg}
                      </FormHelperText>
                    </Box>
                  )}
                  {successMsg && (
                    <Box mt={2} className={classes.successStyle}>
                      {successMsg}
                    </Box>
                  )}
                  <Box
                    mt={successMsg ? 2 : 5}
                    mb={4}
                    display="flex"
                    justifyContent="center"
                  >
                    <Button
                      className={classes.buttonStyle}
                      color="primary"
                      type="submit"
                      variant="contained"
                    >
                      Submit
                    </Button>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Container>
        )}
      </form>
    </>
  );
};

export default AccountActivation;
